/**
 * Tenant web-app metadata
 *
 * index.html and manifest.json are static files baked at build time with
 * Konklave defaults, but both tenants are served from the same build. This
 * module rewrites the document head at runtime so the browser tab, favicon,
 * and "Add to Home Screen" / PWA install flow all reflect the tenant
 * resolved from the hostname.
 *
 * The manifest is injected as a data: URL because a single static file can't
 * vary by domain. All URLs inside it must be absolute — relative paths can't
 * resolve against a data: base.
 *
 * Per-tenant icons live in public/icons/<orgId>/ (generated from the mobile
 * app icon sources). When adding a tenant, generate icon-64/180/192/512.png
 * there.
 */
import { OrganizationConfig } from './tenant';

function setLink(rel: string, href: string, attrs: Record<string, string> = {}): void {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
  Object.entries(attrs).forEach(([name, value]) => link!.setAttribute(name, value));
}

function setMeta(name: string, content: string): void {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

const TENANT_ICON_DIRS = new Set(['kappa', 'dst']);

export function applyTenantWebAppMetadata(config: OrganizationConfig, orgId: string): void {
  const origin = window.location.origin;
  // Only the launch tenants ship their own icon set; everyone else (the platform
  // console + self-serve communities) wears the Konklave icons.
  const iconDir = TENANT_ICON_DIRS.has(orgId) ? orgId : 'konklave';
  const iconBase = `${origin}/icons/${iconDir}`;

  document.title = config.appName;
  setMeta('theme-color', config.branding.primaryColor);
  setMeta(
    'description',
    `${config.appName} - Connect with ${config.name} members, browse chapters, attend events, and stay engaged with your community.`,
  );

  // Favicons — index.html ships Konklave's; point every icon link at the tenant set.
  document.head
    .querySelectorAll<HTMLLinkElement>('link[rel="icon"]')
    .forEach(link => link.remove());
  setLink('icon', `${iconBase}/icon-64.png`, { type: 'image/png', sizes: '64x64' });

  // iOS reads these from the live DOM at add-to-home-screen time.
  setLink('apple-touch-icon', `${iconBase}/icon-180.png`);
  setMeta('apple-mobile-web-app-title', config.appName);

  const manifest = {
    name: config.appName,
    short_name: config.appName,
    icons: [
      { src: `${iconBase}/icon-192.png`, type: 'image/png', sizes: '192x192' },
      { src: `${iconBase}/icon-512.png`, type: 'image/png', sizes: '512x512' },
    ],
    start_url: `${origin}/`,
    scope: `${origin}/`,
    display: 'standalone',
    theme_color: config.branding.primaryColor,
    background_color: config.branding.backgroundColor || '#FFFFFF',
  };
  setLink(
    'manifest',
    `data:application/manifest+json;charset=utf-8,${encodeURIComponent(JSON.stringify(manifest))}`,
  );
}
